import { useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";

import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import {
  buildReportDefaultParams,
  isReportDefaultParamsFailure,
} from "pages/secure/shared/reports/buildReportDefaultParams";
import {
  clearReportAutoRunNavigation,
  isReportAutoRunRequested,
} from "pages/secure/shared/reports/reportAutoRun/buildReportRunUrl";
import { useReportAutoRunContext } from "pages/secure/shared/reports/reportAutoRun/ReportAutoRunContext";
import { mapReportRunParamsToState } from "pages/secure/shared/reports/reportAutoRun/mapReportRunParamsToState";
import { getPresetReportAutoRunParams } from "pages/secure/shared/reports/reportAutoRun/reportAutoRun.types";
import { toReportRunParams } from "pages/secure/shared/reports/reportAutoRun/toReportRunParams";
import { TReportAdminScope } from "pages/secure/shared/reports/reportCatalog.types";

interface IUseReportAutoRunOptions<TState> {
  scope?: TReportAdminScope;
  reportCode?: string;
  reportId: number | null;
  isDedicatedRoute?: boolean;
  isReady: boolean;
  setState: React.Dispatch<React.SetStateAction<TState>>;
  run: () => void | Promise<void>;
}

async function resolveReportAutoRunParams(options: {
  scope: TReportAdminScope;
  reportCode: string;
  reportId: number;
  isDedicatedRoute: boolean;
  presetParams?: Record<string, unknown>;
}) {
  if (options.presetParams) {
    return { ok: true as const, params: options.presetParams };
  }

  return buildReportDefaultParams({
    scope: options.scope,
    reportId: options.reportId,
    code: options.reportCode,
    isDedicatedRoute: options.isDedicatedRoute,
  });
}

export function useReportAutoRun<TState extends Record<string, unknown>>(
  options: IUseReportAutoRunOptions<TState>,
): void {
  const { reportId, isReady, setState, run } = options;
  const context = useReportAutoRunContext();
  const scope = options.scope ?? context?.scope;
  const reportCode = options.reportCode ?? context?.reportCode;
  const isDedicatedRoute = options.isDedicatedRoute ?? context?.isDedicatedRoute ?? false;
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const hasRunRef = useRef(false);
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    if (
      !isReportAutoRunRequested(location.search) ||
      !isReady ||
      reportId == null ||
      !scope ||
      !reportCode ||
      hasRunRef.current
    ) {
      return;
    }

    hasRunRef.current = true;

    void (async () => {
      const paramsResult = await resolveReportAutoRunParams({
        scope,
        reportCode,
        reportId,
        isDedicatedRoute,
        presetParams: getPresetReportAutoRunParams(location.state),
      });

      if (isReportDefaultParamsFailure(paramsResult)) {
        dispatch(showError(paramsResult.errorMessage));
        history.replace(clearReportAutoRunNavigation(location.pathname, location.search));
        return;
      }

      const statePatch = mapReportRunParamsToState(toReportRunParams(paramsResult.params));

      flushSync(() => {
        setState(currentState => ({
          ...currentState,
          ...statePatch,
        }));
      });

      history.replace(clearReportAutoRunNavigation(location.pathname, location.search));
      await runRef.current();
    })();
  }, [
    dispatch,
    history,
    isDedicatedRoute,
    isReady,
    location.pathname,
    location.search,
    location.state,
    reportCode,
    reportId,
    scope,
    setState,
  ]);
}
